import {DatePicker, Form, Input, Select, Spin} from 'antd';
import styles from './settleOrder.module.scss';
import {stockOrderStatus, walletCurrencies} from 'utils/mock';
import backIcon from 'images/back.png';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {useMutation, useQuery} from '@apollo/client';
import {
  GET_ALL_BROKERS,
  GET_ALL_STOCKS,
  GET_STOCKS_ORDER_BY_ID,
} from 'graphql/queries/stocks';
import {brokerOptionLabel} from '../brokerVenues';
import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {SETTLE_ORDER} from 'graphql/mutations/stocks';
import {toast} from 'react-toastify';
import {Button} from '@dabafinance/react-components';
import {ConfirmModal, OrderNotes} from '../ViewOrders/HandleOrders';
import moment from 'moment';

const SettleOrder = () => {
  const [formValues, setFormValues] = useState({open: false, values: null});
  const [noteModal, setNoteModal] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const {id} = useParams();

  const {data, loading, refetch} = useQuery(GET_STOCKS_ORDER_BY_ID, {
    variables: {input: {id, limit: 1}},
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error.message),
  });

  const [settleOrder, {loading: loadingUpdate}] = useMutation(SETTLE_ORDER, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });
  const {data: getAllBrokers, loading: loadingAllBrokers} = useQuery(
    GET_ALL_BROKERS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  useEffect(() => {
    if (data?.getOrders?.data?.[0]) {
      const orderData = data.getOrders.data[0];
      const {
        direction,
        fillQuantity,
        type,
        quantity,
        commissionFee,
        limitPrice,
        exitFee,
        recurringFee,
        feeCharged,
        staticFee,
        symbol,
        broker,
        floatingFee,
        entryFee,
        assetCurrency,
      } = orderData;
      form.setFieldsValue({
        direction,
        fillQuantity: quantity - fillQuantity,
        assetCurrency,
        entryFee: entryFee?.toFixed(2) ?? 0,
        commissionFee: commissionFee?.toFixed(2) ?? 0,
        exitFee: exitFee?.toFixed(2) ?? 0,
        recurringFee: recurringFee?.toFixed(2) ?? 0,
        totalFee: feeCharged?.toFixed(2) ?? 0,
        broker: broker?.id,
        floatingFee: floatingFee?.toFixed(2) ?? 0,
        staticFee: staticFee?.toFixed(2) ?? 0,
        type,
        price: limitPrice,
        ticker: symbol,
      });
    }
  }, [data?.getOrders]);

  const handleSettleOrder = values => {
    const {
      status,
      direction,
      fillQuantity,
      type,
      price,
      ticker,
      fillDate,
      company,
      assetCurrency,
      broker,
      brokerOrderId,
    } = values;
    settleOrder({
      variables: {
        input: {
          status,
          orderId: id,
          lastFillDate: fillDate ? moment(fillDate).format() : null,
          direction,
          fillQuantity: Number(fillQuantity),
          type,
          ...(broker && {broker}),
          ...(brokerOrderId && {brokerOrderId}),
          assetPrice: Number(price),
          ticker,
          company,
          currency: assetCurrency,
        },
      },
      refetchQueries: [
        {
          query: GET_ALL_STOCKS,
          variables: {
            page: 1,
            'paginate': true,
            'limit': 10,
            'newFirst': true,
            openOrders: true,
            includeOrdersWithElapsedExpiry: true,
          },
        },
      ],
    })
      .then(({data: settleOrder}) => {
        const {statusCode, __typename, message} = settleOrder.settleOrder;
        if (__typename === 'Error' || statusCode === 400) {
          toast.error(`Something went wrong!!! - ${message} `);
        } else {
          setFormValues({open: false, values: null});
          toast.success('Filled Successfully');
          navigate(-1);
        }
      })
      .catch(err => toast.error(`Something went wrong!! - ${err}`));
  };

  if (loading) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading Order..</h1>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.top}>
        <img onClick={() => navigate(-1)} src={backIcon} />
        <h1>
          {['OPEN', 'PARTIALLY_FILLED'].includes(
            data?.getOrders?.data?.[0]?.status,
          )
            ? 'Fill'
            : ''}{' '}
          Stock Order
        </h1>
      </div>
      <div className={styles.topButton}>
        <Button onClick={() => setNoteModal(true)} label="View / Edit Notes" />
      </div>
      <Form
        onFinish={values => {
          const orderData = data?.getOrders?.data?.[0];
          if (
            orderData?.instrumentKey === 'MUTUAL_FUND' &&
            values.price !== orderData?.instrument?.currentNetAssetValue
          ) {
            setFormValues({open: true, values});
          } else {
            handleSettleOrder(values);
          }
        }}
        form={form}
        className={styles.form}
        layout="vertical">
        <Form.Item
          className={styles.fieldWidth}
          rules={[{required: true, message: 'This is a required field'}]}
          name="ticker"
          label="Ticker">
          <Input disabled />
        </Form.Item>
        <Form.Item
          className={styles.fieldWidth}
          rules={[{required: true, message: 'This is a required field'}]}
          name="assetCurrency"
          label="Currency">
          <Select disabled>
            {walletCurrencies.map(data => (
              <Select.Option key={data.key}>{data.key}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          className={styles.fieldWidth}
          rules={[{required: true, message: 'This is a required field'}]}
          name="direction"
          label="Direction">
          <Select disabled>
            <Select.Option key="BUY">Buy</Select.Option>
            <Select.Option key="SELL">Sell</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          className={styles.fieldWidth}
          rules={[{required: true, message: 'This is a required field'}]}
          name="type"
          label="Order Type">
          <Select disabled>
            <Select.Option key="LIMIT">Limit</Select.Option>
            <Select.Option key="MARKET">Market</Select.Option>
          </Select>
        </Form.Item>
        <div className={styles.fees}>
          <Form.Item
            className={styles.smallWidth}
            name="staticFee"
            label="Static Fee">
            <Input disabled />
          </Form.Item>
          <Form.Item
            className={styles.smallWidth}
            name="floatingFee"
            label="Floating Fee">
            <Input disabled />
          </Form.Item>
          <Form.Item
            className={styles.smallWidth}
            name="commissionFee"
            label="Commission Fee">
            <Input disabled />
          </Form.Item>
          <Form.Item
            className={styles.smallWidth}
            name="recurringFee"
            label="Recurring Fee">
            <Input disabled />
          </Form.Item>
          <Form.Item
            className={styles.smallWidth}
            name="entryFee"
            label="Entry Fee">
            <Input disabled />
          </Form.Item>
          <Form.Item
            className={styles.smallWidth}
            name="exitFee"
            label="Exit Fee">
            <Input disabled />
          </Form.Item>
          <Form.Item
            className={styles.smallWidth}
            rules={[{required: true, message: 'This is a required field'}]}
            name="totalFee"
            label="Total Fee  Charged">
            <Input disabled />
          </Form.Item>
        </div>
        <Form.Item
          className={styles.fieldWidth}
          rules={[{required: true, message: 'This is a required field'}]}
          name="price"
          label={
            <div>
              Order Asset Price (Current Asset Price:{' '}
              <span style={{color: 'green', fontWeight: 'bold'}}>
                {data?.getOrders?.data?.[0]?.assetCurrency}{' '}
                {data?.getOrders?.data?.[0]?.instrument?.currentMarketPrice ??
                  data?.getOrders?.data?.[0]?.instrument
                    ?.currentNetAssetValue ??
                  data?.getOrders?.data?.[0]?.company?.stock?.lastPrice}
              </span>
              )
            </div>
          }>
          <Input type="number" />
        </Form.Item>
        <Form.Item
          className={styles.fieldWidth}
          rules={[{required: true, message: 'This is a required field'}]}
          name="fillQuantity"
          label={
            <div>
              Fill Quantity Left (Total Quantity:{' '}
              <span style={{color: 'green', fontWeight: 'bold'}}>
                {data?.getOrders?.data?.[0]?.quantity}
              </span>
              )
            </div>
          }>
          <Input type="number" />
        </Form.Item>
        <Form.Item
          className={styles.fieldWidth}
          rules={[{required: true, message: 'This is a required field'}]}
          name="status"
          label="Order Status">
          <Select>
            {stockOrderStatus
              .filter(
                data =>
                  data.key === 'FILLED' || data.key === 'PARTIALLY_FILLED',
              )
              ?.map(data => (
                <Select.Option key={data.key}>{data.name}</Select.Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          className={styles.fieldWidth}
          name="broker"
          label="Select Broker">
          <Select
            className={styles.fieldWidth}
            allowClear
            showSearch
            loading={loadingAllBrokers}>
            {getAllBrokers?.getBrokers?.data?.map(data => (
              <Select.Option key={data?.id}>
                {brokerOptionLabel(data)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          className={styles.fieldWidth}
          name="brokerOrderId"
          label="Broker Order ID">
          <Input />
        </Form.Item>
        <Form.Item
          initialValue={
            data?.getOrders?.data?.[0]?.lastFillDate
              ? moment(
                  data?.getOrders?.data?.[0]?.lastFillDate,
                  'YYYY-MM-DD HH:mm:ss',
                )
              : ''
          }
          name="fillDate"
          label="Filled Date">
          <DatePicker
            defaultValue={
              data?.getOrders?.data?.[0]?.lastFillDate
                ? moment(
                    data?.getOrders?.data?.[0]?.lastFillDate,
                    'YYYY-MM-DD HH:mm:ss',
                  )
                : ''
            }
            showTime={{
              defaultValue: moment('00:00', 'HH:mm:ss'),
            }}
            format={'YYYY-MM-DD HH:mm:ss'}
            style={{width: 340}}
          />
        </Form.Item>
        {!['FILLED', 'SETTLED'].includes(
          data?.getOrders?.data?.[0]?.status,
        ) && <SubmitButton disabled={loadingUpdate} label="Fill Order" />}
      </Form>
      <OrderNotes
        orderModal={noteModal}
        setOrderModal={setNoteModal}
        notes={{
          id: data?.getOrders?.data?.[0]?.id,
          notes: data?.getOrders?.data?.[0]?.notes,
        }}
        refetch={refetch}
      />
      <ConfirmModal
        openModal={formValues.open}
        setOpenModal={() => setFormValues({open: false, values: null})}
        title="Warning"
        confirmText="The price you entered is different from the Current Net Asset Value. Are you sure you want to proceed with filling this order?"
        loading={loadingUpdate}
        handleOperation={() => handleSettleOrder(formValues.values)}
      />
    </div>
  );
};

export default SettleOrder;
